import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Button,
  Divider,
  MenuItem,
  Menu,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  CircularProgress,
  Tooltip,
  TablePagination,
  Link,
  useMediaQuery,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  ButtonBase,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import {
  getOrders,
  getOrder,
  updateOrder,
  listOrderTypes,
  listOrderStatusDefinitions,
  softDeleteOrder,
} from '../../../app/api';
import { buildOrderUpdatePayloadFromOrderDetail } from '../private/buildOrderUpdatePayload';
import { migrateOrderDraftMediaToHistory } from '../private/migrateOrderDraftMediaToHistory';
import ConfirmDialog from '../../core/public/ConfirmDialog';
import ListToolbar from '../../core/public/ListToolbar';
import { getUserPreferences, updateUserPreferences } from '../../../app/userPreferences';
import { useTranslation } from '../../../app/LanguageContext';
import { useWorkspace } from '../../../app/WorkspaceContext';
import { getLastUsedOrderTypeId, pickActiveOrderTypeId } from '../private/lastOrderTypeStorage';
import { formatDateTime, formatDate } from '../../../app/date';
import { formatCurrency } from '../../../app/currency';
import { statusColorChipSx } from '../../../app/statusColorChip';
import { PRODUCT_DEFAULT_IMAGE } from '../../medias/public/MediaGallery';
import OrderQuoteExportDialog from '../private/OrderQuoteExportDialog';

const ORDERS_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const statusColors = {
  DRAFT: 'default',
  SENT: 'info',
  APPROVED: 'success',
  CONFIRMED: 'success',
  IN_PREPARATION: 'info',
  DELIVERED: 'primary',
  REJECTED: 'error',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

// null = "não definido pelo usuário" (mostra todos); array = seleção explícita
const parseStoredFilter = (stored) => {
  if (!stored) return null;
  try {
    const arr = JSON.parse(stored);
    if (!Array.isArray(arr)) return null;
    return arr;
  } catch {
    return null;
  }
};

const serializeFilter = (arr) => {
  if (arr === null) return '';
  return JSON.stringify(arr);
};

const LIST_SORT_PREF_KEY = 'orders.list_sort';
const SEARCH_DEBOUNCE_MS = 300;

const parseStoredListSort = (stored) => {
  if (!stored || typeof stored !== 'string') {
    return { sort: '', order: 'desc' };
  }
  try {
    const obj = JSON.parse(stored);
    if (!obj || typeof obj !== 'object') {
      return { sort: '', order: 'desc' };
    }
    const sort = typeof obj.sort === 'string' ? obj.sort : '';
    const order = obj.order === 'asc' || obj.order === 'desc' ? obj.order : 'desc';
    if (sort !== '' && sort !== 'delivery_date') {
      return { sort: '', order: 'desc' };
    }
    return { sort, order };
  } catch {
    return { sort: '', order: 'desc' };
  }
};

const serializeListSort = (s) => {
  if (!s.sort) {
    return '';
  }
  return JSON.stringify({ sort: s.sort, order: s.order });
};

const orderEditCellLinkSx = {
  display: 'block',
  px: 2,
  py: 2,
  cursor: 'pointer',
  color: 'inherit',
  '&:hover': {
    color: 'primary.main',
  },
  '&:focus-visible': {
    borderRadius: 1,
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

const statusFilterValueFromDefinition = (d) => {
  const leg = d?.legacy_code;
  if (leg && String(leg).trim()) {
    return String(leg).trim();
  }
  return d.id;
};

const ORDERS_TOOLBAR_BUTTON_SX = {
  height: 36,
  minHeight: 36,
  boxSizing: 'border-box',
};

const SCOPE_PREF_KEY = 'orders.list_scope';
const TYPES_PREF_KEY = 'orders.list_types';
const VIEW_PREF_KEY = 'orders.list_view';
const BOARD_PAGE_SIZE = 100;

const FILTER_TOGGLE_SX = { height: 32, textTransform: 'none', px: 1.5, fontWeight: 500 };

const parseStoredScope = (v) => (v === 'customer' || v === 'internal' ? v : '');
const parseStoredView = (v) => (v === 'board' ? 'board' : 'table');
const parseStoredTypes = (v) => {
  const arr = parseStoredFilter(v);
  return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string' && x.trim()) : [];
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days from today until the delivery date (negative when late); null without a date. */
const daysUntil = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return null;
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due.getTime() - today.getTime()) / DAY_MS);
};

/** Relative, colored delivery label: overdue in red, next 3 days in orange. */
const deliveryInfo = (o, translate) => {
  if (!o.delivery_date) return { label: '—', full: '', color: 'text.secondary', weight: 400 };
  const full = formatDate(o.delivery_date);
  const d = daysUntil(o.delivery_date);
  if (o.is_finished || d === null) return { label: full, full, color: 'text.secondary', weight: 400 };
  if (d < 0) return { label: translate('orders.due_overdue_days', { n: -d }), full, color: 'error.main', weight: 700 };
  if (d === 0) return { label: translate('orders.due_today'), full, color: 'warning.main', weight: 700 };
  if (d === 1) return { label: translate('orders.due_tomorrow'), full, color: 'warning.main', weight: 600 };
  if (d <= 3) return { label: translate('orders.due_in_days', { n: d }), full, color: 'warning.main', weight: 600 };
  if (d <= 7) return { label: translate('orders.due_in_days', { n: d }), full, color: 'text.primary', weight: 400 };
  return { label: full, full, color: 'text.primary', weight: 400 };
};

const SummaryCard = ({ label, value, sub, color, active, onClick, tooltip, testId }) => (
  <Tooltip title={tooltip}>
    <ButtonBase
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        display: 'block',
        textAlign: 'left',
        borderRadius: 2,
        border: '1px solid',
        borderColor: active ? color : 'divider',
        bgcolor: 'background.paper',
        boxShadow: active ? (t) => `inset 0 0 0 1px ${t.palette[color.split('.')[0]]?.main || t.palette.primary.main}` : 'none',
        px: 2,
        py: 1.25,
        transition: 'border-color .15s',
        '&:hover': { borderColor: color },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {sub}
        </Typography>
      ) : null}
    </ButtonBase>
  </Tooltip>
);

const OrdersPage = () => {
  const { translate, language } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusDefinitions, setStatusDefinitions] = useState([]);
  const [listSort, setListSort] = useState({ sort: '', order: 'desc' });
  const [selectedStatuses, setSelectedStatuses] = useState(null);
  const [statusFilterPrefsReady, setStatusFilterPrefsReady] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pendingSoftDelete, setPendingSoftDelete] = useState(null);
  const [statusQuickMenu, setStatusQuickMenu] = useState(null);
  const [statusQuickDetail, setStatusQuickDetail] = useState(null);
  const [statusQuickLoading, setStatusQuickLoading] = useState(false);
  const [statusQuickSaving, setStatusQuickSaving] = useState(false);
  const [activeOrderTypes, setActiveOrderTypes] = useState([]);
  const [orderTypesLoading, setOrderTypesLoading] = useState(true);
  const [newOrderMenuAnchor, setNewOrderMenuAnchor] = useState(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [scope, setScope] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [dueFilter, setDueFilter] = useState('');
  const [storedView, setView] = useState('table');
  const view = isMobile ? 'table' : storedView;
  const [summary, setSummary] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [quoteExportOrderId, setQuoteExportOrderId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const prefTimeouts = useRef({});
  const savePrefsTimeout = useRef(null);
  const saveListSortTimeout = useRef(null);
  const searchDebounceRef = useRef(null);

  const defaultNewOrderTypeId = useMemo(() => {
    if (!activeOrderTypes.length) return '';
    const last = getLastUsedOrderTypeId(currentWorkspace?.id);
    return pickActiveOrderTypeId(activeOrderTypes, null, last);
  }, [activeOrderTypes, currentWorkspace?.id]);

  useEffect(() => {
    if (orderTypesLoading || !typeFilter.length) return;
    const valid = typeFilter.filter((id) => activeOrderTypes.some((t) => t.id === id));
    if (valid.length !== typeFilter.length) {
      setTypeFilter(valid);
    }
  }, [orderTypesLoading, activeOrderTypes, typeFilter]);

  const primaryOrderTypes = useMemo(() => activeOrderTypes.slice(0, 3), [activeOrderTypes]);
  const overflowOrderTypes = useMemo(() => activeOrderTypes.slice(3), [activeOrderTypes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrderTypesLoading(true);
      try {
        const tr = await listOrderTypes(true);
        if (!cancelled) setActiveOrderTypes(tr.data || []);
      } catch (_) {
        if (!cancelled) setActiveOrderTypes([]);
      } finally {
        if (!cancelled) setOrderTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listOrderStatusDefinitions();
        if (!cancelled) setStatusDefinitions(res.data || []);
      } catch (_) {
        if (!cancelled) setStatusDefinitions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setStatusFilterPrefsReady(true);
      setSelectedStatuses(null);
      setListSort({ sort: '', order: 'desc' });
      setScope('');
      setTypeFilter([]);
      setView('table');
      setOrders([]);
      setTotalOrders(0);
      return;
    }
    let cancelled = false;
    setStatusFilterPrefsReady(false);
    setSelectedStatuses(null);
    setListSort({ sort: '', order: 'desc' });
    (async () => {
      try {
        const res = await getUserPreferences(currentWorkspace.id);
        if (!cancelled) {
          const prefs = res.data?.preferences || {};
          setSelectedStatuses(parseStoredFilter(prefs['orders.status_filter'] || ''));
          setListSort(parseStoredListSort(prefs[LIST_SORT_PREF_KEY] || ''));
          setScope(parseStoredScope(prefs[SCOPE_PREF_KEY]));
          setTypeFilter(parseStoredTypes(prefs[TYPES_PREF_KEY] || ''));
          setView(parseStoredView(prefs[VIEW_PREF_KEY]));
        }
      } catch (_) {
      } finally {
        if (!cancelled) {
          setStatusFilterPrefsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  const saveStatusFilter = (arr) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(savePrefsTimeout.current);
    savePrefsTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        'orders.status_filter': serializeFilter(arr),
      }).catch(() => {});
    }, 500);
  };

  const saveListSortPreference = (next) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(saveListSortTimeout.current);
    saveListSortTimeout.current = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, {
        [LIST_SORT_PREF_KEY]: serializeListSort(next),
      }).catch(() => {});
    }, 500);
  };

  const savePref = (key, value) => {
    if (!currentWorkspace?.id) return;
    clearTimeout(prefTimeouts.current[key]);
    prefTimeouts.current[key] = setTimeout(() => {
      updateUserPreferences(currentWorkspace.id, { [key]: value }).catch(() => {});
    }, 500);
  };

  const changeScope = (next) => {
    const v = parseStoredScope(next);
    setScope(v);
    savePref(SCOPE_PREF_KEY, v);
  };

  const changeTypeFilter = (next) => {
    setTypeFilter(next);
    savePref(TYPES_PREF_KEY, next.length ? JSON.stringify(next) : '');
  };

  const changeView = (next) => {
    if (next !== 'table' && next !== 'board') return;
    setView(next);
    savePref(VIEW_PREF_KEY, next);
  };

  const navigateNewOrder = (typeId, extraState = {}) => {
    navigate('/orders/new', { state: { preferredOrderTypeId: typeId, ...extraState } });
  };

  const duplicateOrder = (o) => {
    navigate('/orders/new?import=1', {
      state: {
        preferredOrderTypeId: o.order_type_id || defaultNewOrderTypeId,
        importSourceOrder: { id: o.id, number: o.number, customer_name: o.customer_name || '' },
      },
    });
  };

  const navigateNewOrderFromImport = () => {
    navigate('/orders/new?import=1', { state: { preferredOrderTypeId: defaultNewOrderTypeId } });
  };

  const listLoading = !statusFilterPrefsReady || loading;

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const loadOrders = useCallback(async () => {
    if (!statusFilterPrefsReady || !currentWorkspace?.id) {
      return;
    }
    try {
      setLoading(true);
      const isBoard = view === 'board';
      const q = isBoard
        ? { page: 0, page_size: BOARD_PAGE_SIZE, summary: true }
        : { page, page_size: rowsPerPage, summary: true };
      if (scope) q.scope = scope;
      if (typeFilter.length) q.order_type_id = typeFilter;
      if (dueFilter) q.due = dueFilter;
      if (listSort.sort) {
        q.sort = listSort.sort;
        q.order = listSort.order;
      }
      if (!debouncedSearch) {
        if (selectedStatuses === null) {
        } else if (selectedStatuses.length === 0) {
          q.status_filter = 'none';
        } else {
          q.status = selectedStatuses;
        }
      }
      if (debouncedSearch) {
        q.q = debouncedSearch;
      }
      const response = await getOrders(q);
      setOrders(response.data?.orders || []);
      setTotalOrders(typeof response.data?.total === 'number' ? response.data.total : 0);
      setSummary(response.data?.summary || null);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    listSort.sort,
    listSort.order,
    page,
    rowsPerPage,
    selectedStatuses,
    statusFilterPrefsReady,
    currentWorkspace?.id,
    debouncedSearch,
    scope,
    typeFilter,
    dueFilter,
    view,
  ]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPage(0);
  }, [selectedStatuses, listSort.sort, listSort.order, debouncedSearch, scope, typeFilter, dueFilter]);

  useEffect(() => {
    if (totalOrders <= 0) {
      return;
    }
    const maxPage = Math.max(0, Math.ceil(totalOrders / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalOrders, rowsPerPage, page]);

  const toggleDeliverySort = () => {
    setListSort((s) => {
      const next =
        s.sort !== 'delivery_date'
          ? { sort: 'delivery_date', order: 'asc' }
          : { sort: 'delivery_date', order: s.order === 'asc' ? 'desc' : 'asc' };
      saveListSortPreference(next);
      return next;
    });
  };

  const allCatalogValues = useMemo(
    () =>
      [...statusDefinitions]
        .sort((a, b) => (a.catalog_sort ?? 0) - (b.catalog_sort ?? 0))
        .map((d) => statusFilterValueFromDefinition(d)),
    [statusDefinitions],
  );

  const statusOptions = useMemo(() => {
    const defs = [...statusDefinitions].sort(
      (a, b) => (a.catalog_sort ?? 0) - (b.catalog_sort ?? 0),
    );
    const base = defs.map((d) => ({
      value: statusFilterValueFromDefinition(d),
      label: d.name || d.id,
    }));
    const byVal = new Map(base.map((x) => [x.value, x]));
    if (Array.isArray(selectedStatuses)) {
      for (const s of selectedStatuses) {
        const v = String(s).trim();
        if (!v || byVal.has(v)) {
          continue;
        }
        byVal.set(v, { value: v, label: v });
      }
    }
    return [...byVal.values()];
  }, [statusDefinitions, selectedStatuses]);

  const effectiveSelected = useMemo(
    () => (selectedStatuses === null ? [...allCatalogValues] : [...selectedStatuses]),
    [selectedStatuses, allCatalogValues],
  );

  const allChecked =
    allCatalogValues.length > 0 &&
    allCatalogValues.length === effectiveSelected.length &&
    allCatalogValues.every((v) => effectiveSelected.includes(v));

  const isExplicitFullCatalog =
    selectedStatuses !== null &&
    allCatalogValues.length > 0 &&
    selectedStatuses.length === allCatalogValues.length &&
    allCatalogValues.every((v) => selectedStatuses.includes(v));

  const isFilterActive = selectedStatuses !== null && !isExplicitFullCatalog;

  const applySelection = (next) => {
    const nextArr = [...next];
    const isFull =
      allCatalogValues.length > 0 &&
      nextArr.length === allCatalogValues.length &&
      allCatalogValues.every((v) => nextArr.includes(v));
    if (isFull) {
      setSelectedStatuses(null);
      saveStatusFilter(null);
      return;
    }
    setSelectedStatuses(nextArr);
    saveStatusFilter(nextArr);
  };

  const toggleStatusAll = () => {
    if (allCatalogValues.length === 0) {
      return;
    }
    if (allChecked) {
      applySelection([]);
    } else {
      applySelection(allCatalogValues);
    }
  };

  const toggleStatusOne = (s) => {
    const current = effectiveSelected;
    const next = current.includes(s) ? current.filter((x) => x !== s) : [...current, s];
    applySelection(next);
  };

  const closeStatusQuickMenu = useCallback(() => {
    setStatusQuickMenu(null);
    setStatusQuickDetail(null);
    setStatusQuickLoading(false);
    setStatusQuickSaving(false);
  }, []);

  const openStatusQuickMenu = useCallback(
    async (event, row) => {
      event.preventDefault();
      event.stopPropagation();
      if (row.is_finished) return;
      if (!row.order_type_id) {
        setSnackbar({
          open: true,
          message: translate('orders.list_status_no_type_tooltip'),
          severity: 'info',
        });
        return;
      }
      setStatusQuickMenu({ anchorEl: event.currentTarget, row });
      setStatusQuickDetail(null);
      setStatusQuickLoading(true);
      try {
        const res = await getOrder(row.id);
        const d = res.data;
        if (!d?.status_options?.length) {
          setSnackbar({
            open: true,
            message: translate('orders.list_status_no_transitions'),
            severity: 'info',
          });
          closeStatusQuickMenu();
          return;
        }
        setStatusQuickDetail(d);
      } catch (e) {
        setSnackbar({
          open: true,
          message: e.message || translate('common.error'),
          severity: 'error',
        });
        closeStatusQuickMenu();
      } finally {
        setStatusQuickLoading(false);
      }
    },
    [translate, closeStatusQuickMenu],
  );

  /** Persists a status change built from the order detail; returns true on success. */
  const performStatusChange = useCallback(
    async (orderId, detail, newStatusId) => {
      const payload = buildOrderUpdatePayloadFromOrderDetail(detail, {
        status: newStatusId,
        status_transition_draft_note: '',
      });
      if (!payload.delivery_date) {
        setSnackbar({ open: true, message: translate('orders.delivery_date_required'), severity: 'error' });
        return false;
      }
      try {
        const res = await updateOrder(orderId, payload);
        const hid = res.data?.status_history_id;
        if (hid) {
          await migrateOrderDraftMediaToHistory(orderId, hid);
        }
        setSnackbar({ open: true, message: translate('orders.list_status_change_success'), severity: 'success' });
        return true;
      } catch (e) {
        setSnackbar({ open: true, message: e.message || translate('common.error'), severity: 'error' });
        return false;
      }
    },
    [translate],
  );

  const applyQuickStatus = useCallback(
    async (newStatusId) => {
      if (!statusQuickMenu?.row?.id || !statusQuickDetail) return;
      setStatusQuickSaving(true);
      try {
        const ok = await performStatusChange(statusQuickMenu.row.id, statusQuickDetail, newStatusId);
        if (ok) {
          closeStatusQuickMenu();
          await loadOrders();
        }
      } finally {
        setStatusQuickSaving(false);
      }
    },
    [statusQuickMenu, statusQuickDetail, performStatusChange, loadOrders, closeStatusQuickMenu],
  );

  const [boardBusyId, setBoardBusyId] = useState(null);

  const moveOrderToColumn = useCallback(
    async (row, column) => {
      if (!row || !column?.def || column.key === row.status) return;
      if (row.is_finished || !row.order_type_id) {
        setSnackbar({
          open: true,
          message: translate(row.is_finished ? 'orders.list_status_finished_tooltip' : 'orders.list_status_no_type_tooltip'),
          severity: 'info',
        });
        return;
      }
      setBoardBusyId(row.id);
      try {
        const res = await getOrder(row.id);
        const detail = res.data;
        const target = (detail?.status_options || []).find(
          (opt) => opt.id === column.def.id || (column.def.legacy_code && opt.id === column.def.legacy_code),
        );
        if (!target) {
          setSnackbar({ open: true, message: translate('orders.list_board_transition_not_allowed'), severity: 'warning' });
          return;
        }
        if (await performStatusChange(row.id, detail, target.id)) {
          await loadOrders();
        }
      } catch (e) {
        setSnackbar({ open: true, message: e.message || translate('common.error'), severity: 'error' });
      } finally {
        setBoardBusyId(null);
      }
    },
    [translate, performStatusChange, loadOrders],
  );

  const renderOrderStatusChip = (o, extraSx = {}) => {
    const customSx = statusColorChipSx(o.status_color);
    const canQuick = !o.is_finished && Boolean(o.order_type_id);
    let tooltip = translate('orders.list_status_change_tooltip');
    if (o.is_finished) {
      tooltip = translate('orders.list_status_finished_tooltip');
    } else if (!o.order_type_id) {
      tooltip = translate('orders.list_status_no_type_tooltip');
    }
    const mergedSx = {
      ...extraSx,
      ...(customSx || {}),
      ...(canQuick ? { cursor: 'pointer', '&:hover': { opacity: 0.92 } } : {}),
    };
    const rowBusy = statusQuickSaving && statusQuickMenu?.row?.id === o.id;
    return (
      <Tooltip title={tooltip}>
        <span>
          <Chip
            label={o.status_label || o.status}
            size="small"
            onClick={canQuick ? (ev) => void openStatusQuickMenu(ev, o) : undefined}
            disabled={Boolean(rowBusy)}
            aria-haspopup={canQuick ? 'menu' : undefined}
            {...(!customSx ? { color: statusColors[o.status] || 'default' } : {})}
            sx={mergedSx}
          />
        </span>
      </Tooltip>
    );
  };

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={totalOrders}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      disabled={listLoading}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={translate('orders.pagination_rows_per_page')}
      labelDisplayedRows={({ from, to, count }) =>
        `${from}–${to} ${translate('orders.pagination_of')} ${count}`
      }
    />
  );

  const renderStatusFilterPopover = () => (
    <Popover
      open={Boolean(statusFilterAnchor)}
      anchorEl={statusFilterAnchor}
      onClose={() => setStatusFilterAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ minWidth: 220 }}>
        <List dense disablePadding>
          <ListItemButton onClick={toggleStatusAll} dense>
            <Checkbox
              edge="start"
              checked={allChecked}
              indeterminate={effectiveSelected.length > 0 && !allChecked}
              tabIndex={-1}
              disableRipple
              size="small"
            />
            <ListItemText
              primary={translate('common.all')}
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItemButton>
          <Divider />
          {statusOptions.map(({ value, label }) => (
            <ListItemButton key={value} onClick={() => toggleStatusOne(value)} dense>
              <Checkbox
                edge="start"
                checked={effectiveSelected.includes(value)}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={label} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Popover>
  );

  const renderStatusQuickMenu = () => (
    <Menu
      anchorEl={statusQuickMenu?.anchorEl ?? null}
      open={Boolean(statusQuickMenu)}
      onClose={closeStatusQuickMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { minWidth: 200 } }}
      MenuListProps={{ dense: true }}
    >
      {statusQuickLoading ? (
        <MenuItem disabled>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          {translate('orders.list_status_menu_loading')}
        </MenuItem>
      ) : (
        (statusQuickDetail?.status_options || []).map((opt) => (
          <MenuItem
            key={opt.id}
            disabled={statusQuickSaving}
            onClick={() => void applyQuickStatus(opt.id)}
          >
            {opt.name}
          </MenuItem>
        ))
      )}
    </Menu>
  );

  const renderInternalBadge = (o) =>
    o.is_internal ? (
      <Chip
        icon={<Inventory2OutlinedIcon sx={{ fontSize: '14px !important' }} />}
        label={translate('orders.list_internal_badge')}
        size="small"
        variant="outlined"
        color="secondary"
        sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
      />
    ) : null;

  const renderSummary = () => {
    const openCount = summary?.open_count ?? 0;
    const toggleDue = (d) => setDueFilter((cur) => (cur === d ? '' : d));
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }} data-testid="orders-summary">
        <SummaryCard
          label={translate('orders.list_summary_open')}
          value={summary ? openCount : '—'}
          sub={summary ? formatCurrency(Number(summary.open_value) || 0) : ''}
          color="primary.main"
          active={false}
          onClick={() => setDueFilter('')}
          tooltip={dueFilter ? translate('orders.list_summary_clear_hint') : ''}
          testId="orders-summary-open"
        />
        <SummaryCard
          label={translate('orders.list_summary_overdue')}
          value={summary ? summary.overdue_count : '—'}
          color={summary?.overdue_count ? 'error.main' : 'text.primary'}
          active={dueFilter === 'overdue'}
          onClick={() => toggleDue('overdue')}
          tooltip={translate(dueFilter === 'overdue' ? 'orders.list_summary_clear_hint' : 'orders.list_summary_click_hint')}
          testId="orders-summary-overdue"
        />
        <SummaryCard
          label={translate('orders.list_summary_week')}
          value={summary ? summary.week_count : '—'}
          color={summary?.week_count ? 'warning.main' : 'text.primary'}
          active={dueFilter === 'week'}
          onClick={() => toggleDue('week')}
          tooltip={translate(dueFilter === 'week' ? 'orders.list_summary_clear_hint' : 'orders.list_summary_click_hint')}
          testId="orders-summary-week"
        />
      </Box>
    );
  };

  const typeName = (id) => activeOrderTypes.find((t) => t.id === id)?.name || id;

  const renderActiveFilterChips = () => {
    const chips = [];
    if (scope) {
      chips.push({
        key: 'scope',
        label: translate(scope === 'internal' ? 'orders.list_scope_internal' : 'orders.list_scope_customer'),
        onDelete: () => changeScope(''),
      });
    }
    typeFilter.forEach((id) => {
      chips.push({
        key: `type-${id}`,
        label: typeName(id),
        onDelete: () => changeTypeFilter(typeFilter.filter((x) => x !== id)),
      });
    });
    if (dueFilter) {
      chips.push({
        key: 'due',
        label: translate(dueFilter === 'overdue' ? 'orders.list_due_overdue' : 'orders.list_due_week'),
        onDelete: () => setDueFilter(''),
      });
    }
    if (isFilterActive) {
      chips.push({
        key: 'status',
        label: `${translate('orders.list_chip_status')}: ${effectiveSelected.length}`,
        onClick: (e) => setStatusFilterAnchor(e.currentTarget),
        onDelete: () => applySelection(allCatalogValues),
      });
    }
    if (!chips.length) return null;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1.5 }} data-testid="orders-active-filters">
        {chips.map((c) => (
          <Chip key={c.key} label={c.label} size="small" color="primary" variant="outlined" onClick={c.onClick} onDelete={c.onDelete} />
        ))}
        {chips.length > 1 ? (
          <Button
            size="small"
            onClick={() => {
              changeScope('');
              changeTypeFilter([]);
              setDueFilter('');
              if (isFilterActive) applySelection(allCatalogValues);
            }}
            sx={{ textTransform: 'none' }}
          >
            {translate('orders.list_clear_filters')}
          </Button>
        ) : null}
      </Box>
    );
  };

  const boardColumns = useMemo(() => {
    const defs = [...statusDefinitions].sort((a, b) => (a.catalog_sort ?? 0) - (b.catalog_sort ?? 0));
    const cols = new Map();
    defs.forEach((d) => {
      cols.set(statusFilterValueFromDefinition(d), { key: statusFilterValueFromDefinition(d), def: d, label: d.name || d.id, color: d.color, orders: [] });
    });
    orders.forEach((o) => {
      let col = cols.get(o.status);
      if (!col) {
        col = [...cols.values()].find((c) => c.def?.id === o.status);
      }
      if (!col) {
        col = { key: o.status, def: null, label: o.status_label || o.status, color: o.status_color, orders: [] };
        cols.set(o.status, col);
      }
      col.orders.push(o);
    });
    // Status filter decides which columns appear; populated columns always show.
    return [...cols.values()].filter((c) => c.orders.length > 0 || (c.def && effectiveSelected.includes(c.key)));
  }, [statusDefinitions, orders, effectiveSelected]);

  const renderBoardCard = (o) => {
    const due = deliveryInfo(o, translate);
    const busy = boardBusyId === o.id;
    const draggable = !o.is_finished && Boolean(o.order_type_id) && !busy;
    return (
      <Paper
        key={o.id}
        variant="outlined"
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', o.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onClick={() => navigate(`/orders/${o.id}/edit`)}
        sx={{
          p: 1.25,
          cursor: draggable ? 'grab' : 'pointer',
          opacity: busy ? 0.5 : 1,
          borderLeft: '3px solid',
          borderLeftColor: due.color === 'error.main' ? 'error.main' : due.color === 'warning.main' ? 'warning.main' : 'divider',
          '&:hover': { borderColor: 'primary.main' },
        }}
        data-testid="orders-board-card"
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          #{o.number}
          {o.order_type_name ? ` · ${o.order_type_name}` : ''}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {o.title || '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mt: 0.25 }}>
          {renderInternalBadge(o)}
          <Typography variant="caption" color="text.secondary" noWrap>
            {o.customer_name || ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
          <Tooltip title={due.full}>
            <Typography variant="caption" sx={{ color: due.color, fontWeight: due.weight }}>
              {due.label}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(Number(o.final_value) || 0)}
          </Typography>
        </Box>
      </Paper>
    );
  };

  const renderBoard = () => {
    if (listLoading && !orders.length) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
        </Paper>
      );
    }
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {translate('orders.list_board_hint')}
          {totalOrders > orders.length ? ` ${translate('orders.list_board_truncated')}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, alignItems: 'flex-start', opacity: listLoading ? 0.6 : 1 }} data-testid="orders-board">
          {boardColumns.length === 0 ? (
            <Paper sx={{ p: 3, flex: 1, textAlign: 'center' }}>
              <Typography color="text.secondary">{translate('orders.no_orders')}</Typography>
            </Paper>
          ) : (
            boardColumns.map((col) => {
              const colSx = statusColorChipSx(col.color);
              const isOver = dragOverColumn === col.key;
              return (
                <Box
                  key={col.key}
                  onDragOver={(e) => {
                    if (!col.def) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverColumn !== col.key) setDragOverColumn(col.key);
                  }}
                  onDragLeave={() => setDragOverColumn((cur) => (cur === col.key ? null : cur))}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverColumn(null);
                    const id = e.dataTransfer.getData('text/plain');
                    const row = orders.find((x) => x.id === id);
                    void moveOrderToColumn(row, col);
                  }}
                  sx={{
                    flex: '0 0 272px',
                    bgcolor: isOver ? 'action.selected' : 'action.hover',
                    borderRadius: 2,
                    p: 1,
                    border: '1px dashed',
                    borderColor: isOver ? 'primary.main' : 'transparent',
                    transition: 'background-color .15s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
                    <Chip
                      label={col.label}
                      size="small"
                      {...(!colSx ? { color: statusColors[col.key] || 'default' } : {})}
                      sx={colSx || {}}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {col.orders.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 48 }}>
                    {col.orders.length ? (
                      col.orders.map((o) => renderBoardCard(o))
                    ) : (
                      <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', py: 1.5 }}>
                        {translate('orders.list_board_empty')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    );
  };

  const renderRowMenu = () => {
    const o = rowMenu?.row;
    const close = () => setRowMenu(null);
    return (
      <Menu anchorEl={rowMenu?.anchorEl ?? null} open={Boolean(rowMenu)} onClose={close} MenuListProps={{ dense: true }}>
        <MenuItem onClick={() => { close(); navigate(`/orders/${o.id}/edit`); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {translate('common.edit')}
        </MenuItem>
        <MenuItem onClick={() => { close(); setQuoteExportOrderId(o.id); }}>
          <IosShareIcon fontSize="small" sx={{ mr: 1 }} />
          {translate('orders.list_action_export_quote')}
        </MenuItem>
        <MenuItem onClick={() => { close(); duplicateOrder(o); }}>
          <FileCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {translate('orders.list_action_duplicate')}
        </MenuItem>
        {o?.can_soft_delete ? (
          <MenuItem onClick={() => { close(); setPendingSoftDelete({ id: o.id, number: o.number }); }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {translate('orders.soft_delete_from_list')}
          </MenuItem>
        ) : null}
      </Menu>
    );
  };

  const loadOrderForQuoteExport = async () => {
    const res = await getOrder(quoteExportOrderId);
    return res.data;
  };

  const saveQuoteMetaFromList = async (quote) => {
    const detail = (await getOrder(quoteExportOrderId)).data;
    const payload = buildOrderUpdatePayloadFromOrderDetail(detail, {
      status_transition_draft_note: detail?.status_transition_draft_note || '',
    });
    await updateOrder(quoteExportOrderId, { ...payload, metadata: { quote } });
    return (await getOrder(quoteExportOrderId)).data;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {orderTypesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 36 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeOrderTypes.length === 0 ? (
            <Button variant="contained" startIcon={<AddIcon />} disabled sx={ORDERS_TOOLBAR_BUTTON_SX}>
              {translate('common.new')} {translate('orders.title')}
            </Button>
          ) : (
            <>
              <Box
                component="span"
                role="group"
                aria-label={translate('orders.new_order_split_aria')}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
              >
                {primaryOrderTypes.map((t, index) => (
                  <Button
                    key={t.id}
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigateNewOrder(t.id)}
                    data-testid={index === 0 ? 'orders-new-primary' : undefined}
                    sx={ORDERS_TOOLBAR_BUTTON_SX}
                  >
                    {t.name}
                  </Button>
                ))}
                <Button
                  variant="contained"
                  aria-label={translate('orders.new_order_open_types_menu')}
                  data-testid="orders-new-menu-trigger"
                  onClick={(e) => setNewOrderMenuAnchor(e.currentTarget)}
                  sx={{ ...ORDERS_TOOLBAR_BUTTON_SX, minWidth: 36, px: 1 }}
                >
                  <AddIcon />
                </Button>
              </Box>
              <Menu
                anchorEl={newOrderMenuAnchor}
                open={Boolean(newOrderMenuAnchor)}
                onClose={() => setNewOrderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {overflowOrderTypes.map((t) => (
                  <MenuItem
                    key={t.id}
                    onClick={() => {
                      setNewOrderMenuAnchor(null);
                      navigateNewOrder(t.id);
                    }}
                  >
                    {t.name}
                  </MenuItem>
                ))}
                {overflowOrderTypes.length > 0 ? <Divider /> : null}
                <MenuItem
                  data-testid="orders-new-internal"
                  onClick={() => {
                    setNewOrderMenuAnchor(null);
                    navigateNewOrder(defaultNewOrderTypeId, { preferredIsInternal: true });
                  }}
                >
                  <Inventory2OutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  {translate('orders.new_internal_order')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setNewOrderMenuAnchor(null);
                    navigateNewOrderFromImport();
                  }}
                >
                  {translate('orders.import_from_order_menu')}
                </MenuItem>
              </Menu>
            </>
          )}
          {currentWorkspace ? (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              aria-label={translate('orders.copy_quick_intake_link')}
              sx={ORDERS_TOOLBAR_BUTTON_SX}
              onClick={async () => {
                const qs = new URLSearchParams({ lang: language || 'pt-BR' });
                const url = `${window.location.origin}/solicitacao/${encodeURIComponent(currentWorkspace.id)}?${qs.toString()}`;
                try {
                  await navigator.clipboard.writeText(url);
                  setSnackbar({
                    open: true,
                    message: translate('orders.copy_quick_intake_copied'),
                    severity: 'success',
                  });
                } catch {
                  setSnackbar({
                    open: true,
                    message: translate('orders.copy_quick_intake_failed'),
                    severity: 'error',
                  });
                }
              }}
            >
              {translate('orders.copy_quick_intake_button')}
            </Button>
          ) : null}
        </Box>
      </Box>

      {renderSummary()}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scope || 'all'}
          onChange={(_, v) => v && changeScope(v === 'all' ? '' : v)}
          aria-label={translate('orders.origin')}
          data-testid="orders-scope-filter"
        >
          <ToggleButton value="all" sx={FILTER_TOGGLE_SX}>{translate('orders.list_scope_all')}</ToggleButton>
          <ToggleButton value="customer" sx={FILTER_TOGGLE_SX}>{translate('orders.list_scope_customer')}</ToggleButton>
          <ToggleButton value="internal" sx={FILTER_TOGGLE_SX}>
            <Inventory2OutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {translate('orders.list_scope_internal')}
          </ToggleButton>
        </ToggleButtonGroup>

        {activeOrderTypes.length > 1 ? (
          <ToggleButtonGroup
            size="small"
            value={typeFilter.length ? typeFilter : ['__all__']}
            onChange={(_, v) => {
              const arr = v || [];
              if (typeFilter.length && arr.includes('__all__')) {
                changeTypeFilter([]);
                return;
              }
              const next = arr.filter((x) => x !== '__all__');
              changeTypeFilter(next.length === activeOrderTypes.length ? [] : next);
            }}
            aria-label={translate('orders.order_type')}
            data-testid="orders-type-filter"
          >
            <ToggleButton value="__all__" sx={FILTER_TOGGLE_SX}>{translate('orders.list_type_all')}</ToggleButton>
            {activeOrderTypes.map((t) => (
              <ToggleButton key={t.id} value={t.id} sx={FILTER_TOGGLE_SX}>{t.name}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : null}

        <Button
          size="small"
          startIcon={isFilterActive ? <FilterListOffIcon /> : <FilterListIcon />}
          color={isFilterActive ? 'primary' : 'inherit'}
          variant="outlined"
          onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
          sx={{ ...FILTER_TOGGLE_SX, borderColor: isFilterActive ? undefined : 'divider' }}
        >
          {translate('orders.status')}
          {isFilterActive ? ` (${effectiveSelected.length})` : ''}
        </Button>

        {!isMobile ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, v) => changeView(v)}
            sx={{ ml: 'auto' }}
            data-testid="orders-view-toggle"
          >
            <ToggleButton value="table" sx={FILTER_TOGGLE_SX} aria-label={translate('orders.list_view_table')}>
              <ViewListIcon sx={{ fontSize: 18, mr: 0.5 }} />
              {translate('orders.list_view_table')}
            </ToggleButton>
            <ToggleButton value="board" sx={FILTER_TOGGLE_SX} aria-label={translate('orders.list_view_board')}>
              <ViewKanbanOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
              {translate('orders.list_view_board')}
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
      </Box>

      <ListToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchHelperTextKey="orders.list_search_hint"
      />

      {renderActiveFilterChips()}

      {isMobile ? (
        <>
          {listLoading ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : orders.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">{translate('orders.no_orders')}</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {orders.map((o) => {
                const coverSrc = o.cover_thumb_path ? `${ORDERS_API_URL}${o.cover_thumb_path}` : PRODUCT_DEFAULT_IMAGE;
                const due = deliveryInfo(o, translate);
                return (
                  <Paper key={o.id} sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 0.75 }}>
                      <Avatar
                        variant="rounded"
                        src={coverSrc}
                        alt=""
                        sx={{ width: 56, height: 56, flexShrink: 0, cursor: 'pointer' }}
                        onClick={() => navigate(`/orders/${o.id}/edit`)}
                        imgProps={{
                          onError: (e) => {
                            e.currentTarget.src = PRODUCT_DEFAULT_IMAGE;
                          },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {o.number}
                              {o.order_type_name ? ` · ${o.order_type_name}` : ''}
                            </Typography>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                              {o.title || '—'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              {renderInternalBadge(o)}
                              {o.customer_name && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {o.customer_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          {renderOrderStatusChip(o, { ml: 1, flexShrink: 0 })}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title={due.full}>
                        <Typography variant="caption" sx={{ color: due.color, fontWeight: due.weight }}>
                          {translate('orders.delivery_date')}: {due.label}
                        </Typography>
                      </Tooltip>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(Number(o.final_value) || 0)}
                        </Typography>
                        <IconButton size="small" onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, row: o })} aria-label={translate('orders.actions')}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          {renderPagination()}
        </>
      ) : view === 'board' ? (
        renderBoard()
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell>{translate('orders.list_order_customer')}</TableCell>
                <TableCell>
                  <Tooltip title={translate('orders.list_items_quantity_hint')}>
                    <span>{translate('orders.list_items_quantity')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{translate('orders.status')}</TableCell>
                <TableCell sortDirection={listSort.sort === 'delivery_date' ? listSort.order : false}>
                  <TableSortLabel
                    active={listSort.sort === 'delivery_date'}
                    direction={listSort.sort === 'delivery_date' ? listSort.order : 'asc'}
                    onClick={toggleDeliverySort}
                  >
                    {translate('orders.delivery_date')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">{translate('orders.list_final_value')}</TableCell>
                <TableCell align="right" sx={{ width: 168 }}>{translate('orders.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {translate('common.loading')}
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {translate('orders.no_orders')}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => {
                  const coverSrc = o.cover_thumb_path ? `${ORDERS_API_URL}${o.cover_thumb_path}` : PRODUCT_DEFAULT_IMAGE;
                  const due = deliveryInfo(o, translate);
                  return (
                    <TableRow
                      key={o.id}
                      hover
                      sx={{
                        '& .row-actions': { opacity: 0.35, transition: 'opacity .15s' },
                        '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
                      }}
                    >
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Avatar
                          variant="rounded"
                          src={coverSrc}
                          alt=""
                          sx={{ width: 48, height: 48, cursor: 'pointer' }}
                          component={RouterLink}
                          to={`/orders/${o.id}/edit`}
                          imgProps={{
                            onError: (e) => {
                              e.currentTarget.src = PRODUCT_DEFAULT_IMAGE;
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none" sx={{ maxWidth: 420 }}>
                        <Link
                          component={RouterLink}
                          to={`/orders/${o.id}/edit`}
                          underline="none"
                          color="inherit"
                          title={formatDateTime(o.created_at) ? `${translate('orders.created_at')}: ${formatDateTime(o.created_at)}` : undefined}
                          aria-label={`${translate('common.edit')}, ${translate('orders.number')} ${o.number}`}
                          sx={{ ...orderEditCellLinkSx, py: 1.25, '&:hover .order-title': { color: 'primary.main', textDecoration: 'underline' } }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                            #{o.number}
                            {o.order_type_name ? ` · ${o.order_type_name}` : ''}
                          </Typography>
                          <Typography className="order-title" variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {o.title || '—'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            {renderInternalBadge(o)}
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {o.customer_name || (o.is_internal ? '' : '—')}
                            </Typography>
                          </Box>
                        </Link>
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number.isFinite(Number(o.line_items_count)) ? Number(o.line_items_count) : 0}
                      </TableCell>
                      <TableCell>{renderOrderStatusChip(o)}</TableCell>
                      <TableCell>
                        <Tooltip title={due.full}>
                          <Typography variant="body2" sx={{ color: due.color, fontWeight: due.weight, whiteSpace: 'nowrap' }}>
                            {due.label}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatCurrency(
                          Number.isFinite(Number(o.final_value)) ? Number(o.final_value) : 0,
                        ) || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box className="row-actions" sx={{ display: 'inline-flex' }}>
                          <Tooltip title={translate('orders.list_action_export_quote')}>
                            <IconButton size="small" onClick={() => setQuoteExportOrderId(o.id)}>
                              <IosShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={translate('orders.list_action_duplicate')}>
                            <IconButton size="small" onClick={() => duplicateOrder(o)}>
                              <FileCopyOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={translate('common.edit')}>
                            <IconButton size="small" onClick={() => navigate(`/orders/${o.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {o.can_soft_delete ? (
                            <Tooltip title={translate('orders.soft_delete_from_list')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingSoftDelete({ id: o.id, number: o.number });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {renderPagination()}
        </TableContainer>
      )}

      {renderStatusFilterPopover()}
      {renderStatusQuickMenu()}
      {renderRowMenu()}
      {quoteExportOrderId ? (
        <OrderQuoteExportDialog
          open
          onClose={() => setQuoteExportOrderId(null)}
          orderId={quoteExportOrderId}
          loadOrder={loadOrderForQuoteExport}
          saveQuoteMeta={saveQuoteMetaFromList}
          companyName={currentWorkspace?.name || ''}
          translate={translate}
          notify={(message, severity = 'success') => setSnackbar({ open: true, message, severity })}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingSoftDelete)}
        title={translate('common.confirm_delete_title')}
        message={translate('orders.confirm_soft_delete_order')}
        onConfirm={async () => {
          const row = pendingSoftDelete;
          setPendingSoftDelete(null);
          if (!row?.id) return;
          try {
            await softDeleteOrder(row.id);
            setSnackbar({ open: true, message: translate('orders.soft_delete_success'), severity: 'success' });
            await loadOrders();
          } catch (e) {
            setSnackbar({
              open: true,
              message: e.message || translate('common.error'),
              severity: 'error',
            });
          }
        }}
        onCancel={() => setPendingSoftDelete(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersPage;
